import type { Dirent } from "node:fs";
import { readdir, readFile } from "node:fs/promises";
import { join } from "node:path";
import { findWorkflow } from "@orchestration/definitions/registry/global-registry";
import { readFlowerIndex } from "@orchestration/runtime/artifacts/flower-index-store";
import { isMissingFileError } from "@orchestration/runtime/artifacts/fs-errors";
import type { StepMetrics } from "@orchestration/runtime/artifacts/step-metrics.types";
import {
  resolveWorkflowsDataRoot,
  resolveWorkflowsRoot,
} from "@orchestration/runtime/workflower-home";

export type AnalyzeTokenTotals = {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  total: number;
};

export type AnalyzeCostTotals = {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  total: number;
};

export type AnalyzeTotals = {
  durationMs: number;
  tokens: AnalyzeTokenTotals;
  cost: AnalyzeCostTotals;
  errorCount: number;
};

export type AnalyzeStepEntry = {
  /** 0-based index of this step across the entire garden, flower order then step order. */
  globalOrder: number;
  /** Flower instance directory name, e.g. "0001-my-workflow" — the identity used for grouping/colors. */
  flowerName: string;
  /** 0-based position of the flower within the garden's flower list. */
  flowerOrder: number;
  workflowId: string;
  stepLabel: string;
  metrics: StepMetrics;
};

export type AnalyzeFlowerSummary = {
  flowerName: string;
  flowerOrder: number;
  workflowId: string;
  stepCount: number;
  totals: AnalyzeTotals;
};

export type AnalyzeGardenResult = {
  gardenName: string;
  steps: AnalyzeStepEntry[];
  flowers: AnalyzeFlowerSummary[];
  totals: AnalyzeTotals;
};

export async function analyzeGarden(cwd: string, gardenName: string): Promise<AnalyzeGardenResult> {
  const gardenPath = join(resolveWorkflowsRoot(cwd), gardenName);
  const dataGardenPath = join(resolveWorkflowsDataRoot(cwd), gardenName);

  const flowerNames = await listFlowerDirNames(gardenPath);

  const steps: AnalyzeStepEntry[] = [];
  const flowers: AnalyzeFlowerSummary[] = [];
  let globalOrder = 0;

  for (let flowerOrder = 0; flowerOrder < flowerNames.length; flowerOrder++) {
    const flowerName = flowerNames[flowerOrder];
    const index = await readFlowerIndex(join(gardenPath, flowerName));
    const workflowId = index?.workflowId ?? "";
    const workflow = workflowId ? findWorkflow(workflowId) : undefined;
    const stepMetricsList = await readMetrics(join(dataGardenPath, flowerName));

    const flowerTotals = createEmptyTotals();

    for (const stepMetrics of stepMetricsList) {
      steps.push({
        globalOrder: globalOrder++,
        flowerName,
        flowerOrder,
        workflowId,
        stepLabel: resolveStepLabel(workflow, stepMetrics),
        metrics: stepMetrics,
      });
      addStepMetricsToTotals(flowerTotals, stepMetrics);
    }

    flowers.push({
      flowerName,
      flowerOrder,
      workflowId,
      stepCount: stepMetricsList.length,
      totals: flowerTotals,
    });
  }

  const totals = createEmptyTotals();
  for (const flower of flowers) addTotals(totals, flower.totals);

  return { gardenName, steps, flowers, totals };
}

async function listFlowerDirNames(gardenPath: string): Promise<string[]> {
  let entries: Dirent[];
  try {
    entries = await readdir(gardenPath, { withFileTypes: true });
  } catch (error) {
    if (isMissingFileError(error)) return [];
    throw error;
  }

  return entries
    .filter((entry) => entry.isDirectory())
    .map((entry) => entry.name)
    .sort((a, b) => a.localeCompare(b));
}

async function readMetrics(dataFlowerPath: string): Promise<StepMetrics[]> {
  let content: string;
  try {
    content = await readFile(join(dataFlowerPath, "metrics.json"), "utf8");
  } catch (error) {
    if (isMissingFileError(error)) return [];
    throw error;
  }

  const parsed: unknown = JSON.parse(content);
  return Array.isArray(parsed) ? (parsed as StepMetrics[]) : [];
}

function resolveStepLabel(
  workflow: { steps: { id: string }[] } | undefined,
  stepMetrics: StepMetrics,
): string {
  const definitionStep = workflow?.steps[stepMetrics.stepIndex];
  return definitionStep?.id ?? stepMetrics.stepId;
}

function createEmptyTotals(): AnalyzeTotals {
  return {
    durationMs: 0,
    tokens: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0, total: 0 },
    cost: { input: 0, output: 0, cacheRead: 0, cacheWrite: 0, total: 0 },
    errorCount: 0,
  };
}

function addStepMetricsToTotals(totals: AnalyzeTotals, stepMetrics: StepMetrics): void {
  totals.durationMs += stepMetrics.durationMs;
  totals.errorCount += stepMetrics.errorCount;

  const usage = stepMetrics.usage;
  if (!usage) return;

  totals.tokens.input += usage.input;
  totals.tokens.output += usage.output;
  totals.tokens.cacheRead += usage.cacheRead;
  totals.tokens.cacheWrite += usage.cacheWrite;
  totals.tokens.total += usage.totalTokens;

  totals.cost.input += usage.cost.input;
  totals.cost.output += usage.cost.output;
  totals.cost.cacheRead += usage.cost.cacheRead;
  totals.cost.cacheWrite += usage.cost.cacheWrite;
  totals.cost.total += usage.cost.total;
}

function addTotals(target: AnalyzeTotals, source: AnalyzeTotals): void {
  target.durationMs += source.durationMs;
  target.errorCount += source.errorCount;

  target.tokens.input += source.tokens.input;
  target.tokens.output += source.tokens.output;
  target.tokens.cacheRead += source.tokens.cacheRead;
  target.tokens.cacheWrite += source.tokens.cacheWrite;
  target.tokens.total += source.tokens.total;

  target.cost.input += source.cost.input;
  target.cost.output += source.cost.output;
  target.cost.cacheRead += source.cost.cacheRead;
  target.cost.cacheWrite += source.cost.cacheWrite;
  target.cost.total += source.cost.total;
}
